import type { Job } from "../types";
import { DOSSIER_TYPE_LABELS } from "./dossierLabels";
import { getJobDisplayStatus } from "./jobStatus";

export type JobCategory = "done" | "to_schedule" | "in_progress" | "all";

export const JOB_CATEGORY_LABELS: Record<JobCategory, string> = {
  done: "Terminé",
  to_schedule: "À programmer",
  in_progress: "En cours",
  all: "Tous les dossiers",
};

export const JOB_CATEGORY_ORDER: JobCategory[] = [
  "to_schedule",
  "in_progress",
  "done",
  "all",
];

/** Filtre type après « À programmer » : tous les types connus + sans type. */
export type DossierTypeFilter = NonNullable<Job["dossierType"]> | "__none__";

export const DOSSIER_TYPE_FILTER_ORDER: DossierTypeFilter[] = [
  ...(Object.keys(DOSSIER_TYPE_LABELS) as Array<NonNullable<Job["dossierType"]>>),
  "__none__",
];

export const DOSSIER_TYPE_FILTER_LABELS: Record<DossierTypeFilter, string> = {
  ...DOSSIER_TYPE_LABELS,
  __none__: "Sans type",
};

/** Catégories qui imposent un 2e clic par type de dossier. */
export function categoryRequiresDossierType(category: JobCategory): boolean {
  return category === "to_schedule";
}

export function jobMatchesCategory(
  job: Pick<Job, "status" | "date">,
  category: JobCategory
): boolean {
  if (category === "all") return true;
  const displayStatus = getJobDisplayStatus(job);
  switch (category) {
    case "to_schedule":
      return displayStatus === "pending" || displayStatus === "scheduled";
    case "in_progress":
      return displayStatus === "in_progress";
    case "done":
      return displayStatus === "completed" || displayStatus === "cancelled";
    default:
      return true;
  }
}

export function getJobDossierTypeFilter(
  job: Pick<Job, "dossierType">
): DossierTypeFilter {
  const t = job.dossierType;
  if (t && t in DOSSIER_TYPE_LABELS) return t;
  return "__none__";
}

export function jobMatchesDossierTypeFilter(
  job: Pick<Job, "dossierType">,
  filter: DossierTypeFilter
): boolean {
  return getJobDossierTypeFilter(job) === filter;
}

export function countJobsByCategory(jobs: Job[]): Record<JobCategory, number> {
  const counts: Record<JobCategory, number> = {
    done: 0,
    to_schedule: 0,
    in_progress: 0,
    all: jobs.length,
  };
  for (const job of jobs) {
    if (jobMatchesCategory(job, "to_schedule")) counts.to_schedule++;
    if (jobMatchesCategory(job, "in_progress")) counts.in_progress++;
    if (jobMatchesCategory(job, "done")) counts.done++;
  }
  return counts;
}

export function countJobsByDossierType(
  jobs: Job[]
): Array<{ type: DossierTypeFilter; label: string; count: number }> {
  const counts = new Map<DossierTypeFilter, number>();
  for (const job of jobs) {
    const key = getJobDossierTypeFilter(job);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return DOSSIER_TYPE_FILTER_ORDER.filter((type) => (counts.get(type) ?? 0) > 0).map(
    (type) => ({
      type,
      label: DOSSIER_TYPE_FILTER_LABELS[type],
      count: counts.get(type) ?? 0,
    })
  );
}
